import TotalCase from "../../../images/totalCase.svg";
import KgEntered from "../../../images/material_kg.svg";
import TotalOrangeCase from "../../../images/total_orange.svg";
import MaterialOrange from "../../../images/material_orange.svg";
import TotalEmployee from "../../../images/total_emp.svg";
import EmployeeUnloading from "../../../images/emp_unloading.svg";
import EmployeeWashing from "../../../images/emp_washing.svg";
import AbsentEmployee from "../../../images/emp_absent.svg";
import PalletizedCase from "../../../images/palletized_cases.svg";
import NotPalletizedCase from "../../../images/not_palletized_case.svg";
import AcceptedCase from "../../../images/accepted_cases.svg";
import RejectedCase from "../../../images/rejected_case.svg";
import NotWashed from "../../../images/not_washed.svg";
import OrangeIncrease from "../../../images/orange_increase.svg";
import GreenRejected from "../../../images/green_cases_rejected.svg";
import { useMemo, useState } from "react";
import {
  useGetAllWorkingAreasQuery,
  useGetReceivingEmployeeSummaryQuery,
  useGetSummaryQuery,
  useGetUnloadingSummaryQuery,
  useGetWashingAreaSummaryQuery,
  useGetRejectionLogSummaryQuery,
} from "../../../app/globalApi";
import AddEmployeeModal from "../../../components/shared/modalContent/AddEmployeeModal";
import { PlusSvg } from "../../../svgFiles/PlusSvg";
import Button from "../../../components/shared/buttons/button";
import GridCard from "../../../components/shared/gridCard/GridCard";
import DateSelector from "../../../components/shared/fields/DateSelector";
import DateRangeFilter from "../../../components/shared/fields/DateRangeFilter";
import { ReceivingCharts } from "./ReceivingCharts";
import { EditSvg } from "../../../svgFiles/EditSvg";
import { skipToken } from "@reduxjs/toolkit/query";
import GridCardNew from "../../../components/shared/gridCard/GridCardNew";
import useTranslator from "../../../hooks/useTranslator";
import { formatDateAndTime, getCurrentDate } from "../../../helpers/Utils";
import { PROJECT_DATE_FORMAT } from "../../../helpers/constants";
import DashboardSection from "../../../features/dashboard/components/DashboardSection";
import "../../dashboard/components/dashboard-sections.css";
import "./ReceivingDashboard.css";
import { PackageIconSvg } from "../../../svgFiles/PackageIconSvg";
import { TruckIconSvg } from "../../../svgFiles/TruckIconSvg";
import { WashIconSvg } from "../../../svgFiles/WashIconSvg";
import { MapGridIconSvg } from "../../../svgFiles/MapGridIconSvg";
import { OrangeIconSvg } from "../../../svgFiles/OrangeIconSvg";
import { RejectionIconSvg } from "../../../svgFiles/RejectionIconSvg";
import { SnowflakeIconSvg } from "../../../svgFiles/SnowflakeIconSvg";
import ColdRoomDataOverview from "../../dashboard/components/ColdRoomDataOverview";

const ReceivingDashboard = () => {
  const { _t } = useTranslator();  
  const currentDate = getCurrentDate("YYYY-MM-DD");
  const [date, setDate] = useState(currentDate);
  const [startDate, setStartDate] = useState(currentDate);
  const [endDate, setEndDate] = useState(currentDate);
  const [tab, setTab] = useState("dashboard");

  const handleApplyRange = ({ startDate: start, endDate: end }) => {
    setStartDate(start);
    setEndDate(end);
    setDate(start);
  };

  // Receiving Summary Query
  const { data: summaryData } = useGetSummaryQuery({ tab, date });

  // Receiving Overview Items - Grid Box Style
  const receivingListItems = [
    {
      label: _t("cases_entered", "Cases Entered"),
      value: summaryData?.data.total_cases_entered || 0,
      bgColor: "#eff6ff",
    },
    {
      label: _t("total_accepted_cases", "Total Accepted Cases"),
      value: summaryData?.data.total_accepted_cases || 0,
      bgColor: "#f0fdf4",
    },
    {
      label: _t("cases_rejected", "Cases Rejected"),
      value: summaryData?.data.cases_rejected || 0,
      bgColor: "#fee2e2",
    },
    {
      label: _t("cases_paid", "Cases Paid"),
      value: summaryData?.data.cases_paid || 0,
      bgColor: "#dcfce7",
    },
    {
      label: _t("total_orange_cases", "Total Orange Cases"),
      value: summaryData?.data.total_orange_cases || 0,
      bgColor: "#fef3c7",
    },
    {
      label: _t("orange_case_increase", "Orange Case Increase"),
      value: summaryData?.data.orange_case_increase
        ? Number(summaryData?.data.orange_case_increase).toFixed(2) + " %"
        : "0.0 %",
      bgColor: "#fff7ed",
    },
    {
      label: _t("kg_entered", "KG Entered"),
      value: summaryData?.data.material_entered_kg || 0,
      bgColor: "#eff6ff",
      unit: " KG",
    },
    {
      label: _t("kg_accepted", "KG Accepted"),
      value: summaryData?.data.kg_accepted || 0,
      bgColor: "#f0fdf4",
      unit: " KG",
    },
    {
      label: _t("kg_rejected", "KG Rejected"),
      value: summaryData?.data.kg_rejected || 0,
      bgColor: "#fee2e2",
      unit: " KG",
    },
    {
      label: _t("kg_per_case", "KG / Case"),
      value: summaryData?.data.kg_per_case || 0,
      bgColor: "#faf5ff",
      unit: " KG",
    },
  ];

  return (
    <div>
      {/* ── Toolbar with Date Filter ──────────────────────────── */}
      <div className="receiving_dashboard_toolbar align-items-center mb-4">
        <h5 className="receiving_dashboard_toolbar_title m-0">
          {_t("receiving_overview", "Receiving Overview")}
        </h5>
        <div className="receiving_filter_group">          
          <DateRangeFilter setDate={setDate} onApplyRange={handleApplyRange} triggerId="receiving-date-range-filter" />          
        </div>
      </div>

      <div className="d-flex flex-column gap-4">
        {/* ── Receiving Overview: Grid Box Style ────────────────── */}
        <DashboardSection title={<span className="d-flex align-items-center gap-2"><PackageIconSvg size={18} color="#60a5fa" />{_t("receiving_data_title", "Receiving Data")}</span>}>
          <div className="pp-brokering-grid">
            {receivingListItems.map((item, i) => (
              <div key={i} className="pp-brokering-box" style={{ backgroundColor: item.bgColor }}>
                <div className="pp-brokering-box-label">{item.label}</div>
                <div className="pp-brokering-box-value">{item.value}{item.unit || ''}</div>
              </div>
            ))}
          </div>
        </DashboardSection>

        {/* ── Cold + Room Data Overview ──────────────────────────────── */}
        <DashboardSection title={<span className="d-flex align-items-center gap-2"><SnowflakeIconSvg size={18} color="#60a5fa" />{_t("cold_room_data_overview_title", "Cold + Room Data Overview")}</span>}>
          <ColdRoomDataOverview />
        </DashboardSection>
      </div>
    </div>
  );
};

export default ReceivingDashboard;